// src/sot/primitives/AvatarHeaderPositioned.jsx
import React from "react";
import { useAuth } from "@/contexts/AuthContext.jsx";

// The positioned primitive owns the avatar frame.  The regular placeholder
// SVG includes its own frame/shadow, which produces a nested card here; use
// the existing borderless artwork so there is exactly one visible frame.
const PATIENT_AVATAR_PLACEHOLDER = "/icons/Profile_Avatar_borderless.svg";
const DOCTOR_AVATAR_PLACEHOLDER = "/icons/Profile_Avatar_borderless.svg";

const EDIT_CHIP_ICON = "/icons/doctor/avatar_edit_button.svg";

export default function AvatarHeaderPositioned({
  previewUrl,
  onClick,
  editIconSrc = EDIT_CHIP_ICON,
  mobileOffsetClassName = "pt-[144px] sm:pt-[148px]",
  dataTestId,
  interactive = true,
  showEditChip = true,
  alt = "Profile",
  railClassName = "afrimed-rail afrimed-rail--comfortable",
  placeholderSrc = "",
  children = null,
}) {
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const profile = auth.profile || {};

  const role = profile.role || auth.role || auth.user?.role || null;

  const placeholder = role === "doctor" ? DOCTOR_AVATAR_PLACEHOLDER : PATIENT_AVATAR_PLACEHOLDER;

  const finalPreview = String(previewUrl || "").trim();
  const hasCustomPhoto = Boolean(finalPreview);
  const fallbackSrc = String(placeholderSrc || "").trim() || placeholder;
  const [failedSource, setFailedSource] = React.useState("");
  const src = failedSource === finalPreview || !hasCustomPhoto ? fallbackSrc : finalPreview;

  // Keep one frame and one image node throughout hydration. A known signed or
  // cached avatar is painted immediately; only a genuine image error swaps
  // that same image node to the approved fallback.
  React.useEffect(() => {
    setFailedSource("");
  }, [finalPreview]);

  return (
    <div className="pointer-events-none absolute inset-0">
      <div
        className={[
          railClassName,
          mobileOffsetClassName,
          "md:pt-[224px] lg:pt-[248px]",
        ].join(" ")}
      >
        <div
          className={interactive ? "pointer-events-auto inline-flex relative" : "inline-flex relative"}
          role={interactive ? undefined : "img"}
          aria-label={interactive ? "Edit profile photo" : alt}
          onClick={interactive ? onClick : undefined}
        >
          {children || <div
            data-testid={dataTestId}
            className="
              afrimed-profile-avatar
              h-20 w-20 md:h-[150px] md:w-[150px]
            "
          >
            <img
              src={src}
              alt={alt}
              className={src === fallbackSrc ? "object-contain" : "object-cover"}
              onError={() => {
                if (hasCustomPhoto) setFailedSource(finalPreview);
              }}
              draggable="false"
            />
          </div>}

          {showEditChip ? <span className="absolute -bottom-1 -right-1 z-10 h-7 w-7">
            <img
              src={editIconSrc}
              alt=""
              className="h-full w-full"
              draggable="false"
            />
          </span> : null}
        </div>
      </div>
    </div>
  );
}
